/**
 * Utility functions for geospatial operations
 */

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param {Array<number>} point - [latitude, longitude]
 * @param {Array<Array<number>>} polygon - Array of [latitude, longitude] pairs
 * @returns {boolean} - True if point is inside polygon
 */
export function pointInPolygon(point, polygon) {
  if (!point || !polygon || polygon.length < 3) {
    return false;
  }

  const [lat, lon] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [latI, lonI] = polygon[i];  // [lat, lon]
    const [latJ, lonJ] = polygon[j];  // [lat, lon]
    
    // Ray casting algorithm: check if horizontal ray from point intersects edge
    // Check if point's latitude is between edge vertices and ray intersects edge
    const intersect = ((latI > lat) !== (latJ > lat)) && 
                      (lon < (lonJ - lonI) * (lat - latI) / (latJ - latI) + lonI);
    
    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Check if a point is inside any polygon in a GeoJSON Feature or FeatureCollection
 * @param {Array<number>} point - [latitude, longitude]
 * @param {Object} geoJson - GeoJSON Feature or FeatureCollection
 * @returns {boolean} - True if point is inside any polygon
 */
export function pointInGeoJSON(point, geoJson) {
  if (!point || !geoJson) {
    return false;
  }

  const [lat, lon] = point;

  // Handle FeatureCollection
  if (geoJson.type === 'FeatureCollection' && Array.isArray(geoJson.features)) {
    return geoJson.features.some(feature => pointInGeoJSON(point, feature));
  }

  // Handle Feature
  if (geoJson.type === 'Feature' && geoJson.geometry) {
    return pointInGeoJSON(point, geoJson.geometry);
  }

  // Handle Geometry
  if (geoJson.type === 'Polygon' && Array.isArray(geoJson.coordinates)) {
    // Coordinates for Polygon is an array of rings, first ring is exterior
    const exteriorRing = geoJson.coordinates[0];
    if (!exteriorRing || !Array.isArray(exteriorRing)) {
      return false;
    }
    // Convert GeoJSON coordinates [lon, lat] to [lat, lon] for our function
    const polygon = exteriorRing.map(coord => [coord[1], coord[0]]);
    return pointInPolygon([lat, lon], polygon);
  }

  // Handle MultiPolygon
  if (geoJson.type === 'MultiPolygon' && Array.isArray(geoJson.coordinates)) {
    return geoJson.coordinates.some(polygonCoords => {
      if (!polygonCoords || !Array.isArray(polygonCoords) || !polygonCoords[0]) {
        return false;
      }
      const exteriorRing = polygonCoords[0];
      const polygon = exteriorRing.map(coord => [coord[1], coord[0]]);
      return pointInPolygon([lat, lon], polygon);
    });
  }

  return false;
}

/**
 * Fetch city/state boundary from Nominatim
 * @param {string} cityName - City name
 * @param {string} stateName - State name (optional)
 * @param {string} countryCode - Country code (default: 'in')
 * @returns {Promise<Object|null>} - GeoJSON boundary or null
 */
export async function fetchCityBoundary(cityName, stateName = '', countryCode = 'in') {
  // Build more specific query for better results
  let query = '';
  if (cityName) {
    // Handle specific cities with better queries
    const cityLower = cityName.toLowerCase();
    if (cityLower === 'mumbai') {
      query = 'Greater Mumbai, Maharashtra, India';
    } else if (cityLower === 'goa' || cityLower === 'goa state') {
      query = 'Goa, India';
    } else {
      // For other cities, use city name with state if available
      if (stateName) {
        query = `${cityName}, ${stateName}, India`;
      } else {
        query = `${cityName}, India`;
      }
    }
  } else if (stateName) {
    query = `${stateName}, India`;
  }
  
  if (!query) {
    return null;
  }

  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&polygon_geojson=1&limit=1&countrycodes=${encodeURIComponent(countryCode)}&q=${encodeURIComponent(query)}`;
    const res = await fetch(url, {
      headers: {
        Accept: 'application/json',
        'User-Agent': 'AtlasApp/1.0 (support@atlas.local)'
      }
    });
    
    if (!res.ok) {
      return null;
    }

    const data = await res.json();
    if (Array.isArray(data) && data.length > 0 && data[0].geojson) {
      return data[0].geojson;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching city boundary:', error);
    return null;
  }
}

/**
 * Cache for city boundaries to avoid repeated API calls
 */
const cityBoundaryCache = new Map();

/**
 * Get city boundary with caching
 * @param {string} cityName - City name
 * @param {string} stateName - State name (optional)
 * @param {string} countryCode - Country code (default: 'in')
 * @returns {Promise<Object|null>} - GeoJSON boundary or null
 */
export async function getCityBoundary(cityName, stateName = '', countryCode = 'in') {
  const cacheKey = `${cityName || ''}_${stateName || ''}_${countryCode}`;
  
  if (cityBoundaryCache.has(cacheKey)) {
    return cityBoundaryCache.get(cacheKey);
  }

  const boundary = await fetchCityBoundary(cityName, stateName, countryCode);
  if (boundary) {
    cityBoundaryCache.set(cacheKey, boundary);
  }
  
  return boundary;
}

/**
 * Clear the city boundary cache
 */
export function clearCityBoundaryCache() {
  cityBoundaryCache.clear();
}

